import { Radio } from "lucide-react";
import { useState } from "react";
import { useListContext, useNotify, useRefresh } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Contact, LeadSource } from "../types";

// Same 8 values as ContactListFilter.tsx / LeadSourceSelector.tsx — kept as its
// own literal list rather than a shared import, same reasoning as those two:
// each editor's copy/labels can diverge later without cross-breaking.
const LEAD_SOURCE_OPTIONS: { value: LeadSource; label: string }[] = [
  { value: "linkedin_engagement", label: "LinkedIn engagement" },
  { value: "linkedin_network", label: "LinkedIn network" },
  { value: "linkedin_jobs", label: "LinkedIn jobs" },
  { value: "reddit", label: "Reddit" },
  { value: "google_places", label: "Google Places" },
  { value: "chatbot_handoff", label: "Chatbot handoff" },
  { value: "lead_magnet", label: "Lead magnet" },
  { value: "cold_outbound", label: "Cold outbound" },
];

/**
 * Bulk-set Lead Source on the selected contacts. mentorax.lead_snapshots
 * isn't reachable through the contacts resource's own update() (same
 * reasoning as useSetLeadSource.ts, the per-contact version this mirrors) —
 * writes go straight to the Supabase client, upserting one row per selected
 * contact.
 */
export function BulkLeadSourceButton() {
  const notify = useNotify();
  const refresh = useRefresh();
  const { onUnselectItems, selectedIds = [] } = useListContext<Contact>();
  const [open, setOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  if (!selectedIds.length) return null;

  const applyLeadSource = async (leadSource: LeadSource) => {
    setIsApplying(true);
    try {
      const rows = selectedIds.map((id) => ({
        contact_id: Number(id),
        lead_source: leadSource,
      }));
      const { error } = await getSupabaseClient()
        .schema("mentorax")
        .from("lead_snapshots")
        .upsert(rows, { onConflict: "contact_id" });
      if (error) throw error;

      notify("crm.common.bulk_update_success", {
        type: "success",
        messageArgs: {
          _: `Lead source updated for ${selectedIds.length} contacts`,
        },
      });
      setOpen(false);
      onUnselectItems();
      refresh();
    } catch (error) {
      notify("crm.common.bulk_update_error", {
        type: "error",
        messageArgs: { _: "Failed to update lead source" },
      });
      console.error("Bulk lead source update failed:", error);
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9"
        onClick={() => setOpen(true)}
      >
        <Radio />
        Lead Source
      </Button>

      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && setOpen(false)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Set Lead Source</DialogTitle>
            <DialogDescription>
              Applies to all {selectedIds.length} selected contacts.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col space-y-2 items-start">
            {LEAD_SOURCE_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                type="button"
                variant="ghost"
                disabled={isApplying}
                className="px-0 py-0 hover:bg-default dark:hover:bg-default mb-0"
                onClick={() => applyLeadSource(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
